import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { IssuedWebhookEndpoint, NewWebhookEndpoint, WebhookDeliveriesPage, WebhookDelivery, WebhookDeliveryFilter, WebhookDeliveryStatus, WebhookEndpoint, WebhookPing } from '@/api/types';
import { formatAgo } from '@/lib/format';
import { answerLabel, deliveryState, endpointState, eventLabel, OFFERED_EVENTS, replayable, STATUS_LABEL, WEBHOOK_DOCS_URL, whenLabel } from '@/lib/webhooks';
import { ConfirmButton } from './ConfirmDialog';
import { Dialog, Status } from './ui';

/**
 * «Вебхуки» in «Интеграции» (MK42-6388), shared by the client's settings (with `actions`) and the
 * studio's client card (read-only, with «Выключить точку»). The secret is shown once, in a dialog.
 */

const mono = 'font-mono text-xs';

/** The signing secret, once, with how to check a signature. */
export function WebhookSecretDialog({ issued, rotated, onClose }: { issued: IssuedWebhookEndpoint | null; rotated?: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const close = () => (setCopied(false), onClose());
  return (
    <Dialog open={!!issued} onClose={close} title={rotated ? 'Новый секрет подписи' : 'Секрет подписи'}>
      <div className="space-y-3 p-5 text-sm">
        <pre data-testid="webhook-secret" className="whitespace-pre-wrap break-all rounded bg-sunken p-3 font-mono text-sm">
          {issued?.secret}
        </pre>
        <p role="alert">Скопируйте секрет сейчас: он показывается один раз. Потеряете — выпустите новый.</p>
        <p className="text-muted">
          Каждое событие подписано по стандарту Standard Webhooks: заголовки <code className={mono}>webhook-id</code>, <code className={mono}>webhook-timestamp</code>, <code className={mono}>webhook-signature</code>. Ваш сервер считает HMAC-SHA256 от{' '}
          <code className={mono}>id.timestamp.тело</code> этим секретом и сравнивает с подписью; событие старше 5 минут отвергайте.{' '}
          <Link className="underline underline-offset-2" to={WEBHOOK_DOCS_URL}>
            Примеры проверки
          </Link>
          .
        </p>
        {rotated ? (
          <p className="text-muted">Старый секрет подписывает события ещё 24 часа, рядом с новым: замените его на сервере за это время.</p>
        ) : (
          <p className="text-muted">Вебхук включится после «Проверить», когда сервер ответит 2xx.</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              await navigator.clipboard?.writeText(issued?.secret ?? '').catch(() => undefined);
              setCopied(true);
            }}
          >
            {copied ? 'Скопировано' : 'Скопировать'}
          </button>
          <button type="button" className="btn-primary" onClick={close}>
            Готово
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function EndpointForm({ events, pending, onSave, onCancel }: { events: string[]; pending: boolean; onSave: (v: NewWebhookEndpoint) => void; onCancel: () => void }) {
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [chosen, setChosen] = useState<string[]>(['invoice.issued']);
  const httpsOk = /^https:\/\/[^\s/]+/i.test(url.trim());
  const toggle = (t: string) => setChosen((c) => (c.includes(t) ? c.filter((x) => x !== t) : [...c, t]));
  return (
    <form
      className="space-y-3 rounded bg-panel p-4 shadow-hair"
      onSubmit={(e) => {
        e.preventDefault();
        if (!httpsOk || !chosen.length) return;
        onSave({ url: url.trim(), events: chosen, ...(description.trim() ? { description: description.trim() } : {}) });
      }}
    >
      <label className="block text-sm">
        <span className="mb-1.5 block text-muted">Адрес вашего сервера</span>
        <input className="field" type="url" inputMode="url" placeholder="https://example.ru/hooks/mark42" maxLength={500} value={url} onChange={(e) => setUrl(e.target.value)} />
        {url.trim() && !httpsOk && <span className="mt-1 block text-xs text-overdue">Только https://, порт 443 или 8443</span>}
      </label>
      <label className="block text-sm">
        <span className="mb-1.5 block text-muted">Название, если нужно</span>
        <input className="field" placeholder="1С бухгалтерия" maxLength={120} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <fieldset className="space-y-1.5 text-sm">
        <legend className="mb-1.5 text-muted">События</legend>
        {events.map((t) => (
          <label key={t} className="flex items-center gap-2">
            <input type="checkbox" checked={chosen.includes(t)} onChange={() => toggle(t)} />
            <span>{eventLabel(t)}</span>
            <code className="text-xs text-faint">{t}</code>
          </label>
        ))}
      </fieldset>
      <p className="text-xs text-muted">В событии только id и номера, без сумм и имён: подробности ваш сервер забирает ключом API.</p>
      <div className="flex gap-2">
        <button className="btn-primary" disabled={pending || !httpsOk || !chosen.length}>
          Добавить
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

const day = (d: Date) => d.toISOString().slice(0, 10);
const localMidnight = (s: string) => new Date(`${s}T00:00:00`);

function ReplayPeriod({ pending, onReplay, onCancel }: { pending: boolean; onReplay: (since: string, until: string) => void; onCancel: () => void }) {
  const [since, setSince] = useState(day(new Date(Date.now() - 3 * 86_400_000)));
  const [until, setUntil] = useState(day(new Date()));
  const ok = since && until && since <= until;
  return (
    <form
      className="mt-2 flex flex-wrap items-end gap-2 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok) return;
        const end = localMidnight(until);
        end.setDate(end.getDate() + 1);
        onReplay(localMidnight(since).toISOString(), end.toISOString());
      }}
    >
      <label>
        <span className="mb-1 block text-xs text-muted">С</span>
        <input className="field h-8" type="date" value={since} onChange={(e) => setSince(e.target.value)} />
      </label>
      <label>
        <span className="mb-1 block text-xs text-muted">По</span>
        <input className="field h-8" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
      </label>
      <button className="btn-secondary h-8" disabled={pending || !ok}>
        Повторить
      </button>
      <button type="button" className="btn-ghost h-8" onClick={onCancel}>
        Отмена
      </button>
    </form>
  );
}

export type WebhookActions = {
  create: (v: NewWebhookEndpoint) => Promise<IssuedWebhookEndpoint>;
  update: (id: string, patch: { enabled?: boolean }) => Promise<unknown>;
  remove: (id: string) => Promise<unknown>;
  rotate: (id: string) => Promise<IssuedWebhookEndpoint>;
  ping: (id: string) => Promise<WebhookPing>;
  replayFailed: (id: string, since: string, until: string) => Promise<{ queued: number }>;
  pending: boolean;
};

function pingNote(p: WebhookPing): string {
  const d = p.delivery;
  if (d.status === 'delivered') return `Проверка прошла: ${d.responseCode} за ${d.durationMs ?? 0} мс${p.endpoint.enabled ? ', вебхук включён' : ''}.`;
  return `Проверка не прошла: ${answerLabel(d)}${d.durationMs ? ` за ${d.durationMs} мс` : ''}. Сервер должен ответить 2xx.`;
}

/** The endpoints; with `actions` — adding, checking, switching, a new secret, deleting, replays; with `onDisable` — the studio's brake. */
export function WebhookEndpoints({
  endpoints,
  events = OFFERED_EVENTS,
  actions,
  onDisable,
  disabling,
  empty,
}: {
  endpoints: WebhookEndpoint[];
  events?: string[];
  actions?: WebhookActions;
  onDisable?: (id: string) => Promise<unknown>;
  disabling?: boolean;
  empty: string;
}) {
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ ep: IssuedWebhookEndpoint; rotated?: boolean } | null>(null);
  const [notes, setNotes] = useState<Record<string, { text: string; bad?: boolean }>>({});
  const [period, setPeriod] = useState<string | null>(null);
  const [problem, setProblem] = useState('');
  const run = (p: Promise<unknown>) => (setProblem(''), p.catch((e: Error) => setProblem(e.message)));
  const note = (id: string, text: string, bad?: boolean) => setNotes((n) => ({ ...n, [id]: { text, bad } }));
  return (
    <>
      {endpoints.length === 0 ? (
        <p className="text-sm text-muted">{empty}</p>
      ) : (
        <ul className="divide-y divide-line border-y border-line">
          {endpoints.map((ep) => {
            const st = endpointState(ep);
            const needsPing = !ep.enabled && ['unverified', 'ping_failed', 'by_studio'].includes(ep.disabledReason ?? '');
            const n = notes[ep.id];
            return (
              <li key={ep.id} className="py-3" data-testid={`webhook-${ep.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {ep.name && <span className="text-sm font-medium">{ep.name}</span>}
                  <code className="break-all text-xs text-muted">{ep.url}</code>
                  <Status tone={st.tone} className="ml-auto">
                    {st.label}
                  </Status>
                </div>
                <p className="mt-1 text-xs text-muted">{ep.events.map(eventLabel).join(', ')}</p>
                {st.hint && <p className="text-xs text-muted">{st.hint}</p>}
                <p className="text-xs text-faint">
                  {ep.lastSuccessAt ? `последняя доставка ${formatAgo(ep.lastSuccessAt)}` : 'доставок ещё не было'}
                  {ep.secretRotating && ' · старый секрет подписывает ещё до суток'}
                </p>
                {n && (
                  <p role="status" className={n.bad ? 'mt-1 text-sm text-overdue' : 'mt-1 text-sm'}>
                    {n.text}
                  </p>
                )}
                {actions && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <button
                      type="button"
                      className="btn-ghost h-7 px-2 text-xs"
                      disabled={actions.pending}
                      onClick={() => run(actions.ping(ep.id).then((p) => note(ep.id, pingNote(p), p.delivery.status !== 'delivered')))}
                    >
                      Проверить
                    </button>
                    {ep.enabled ? (
                      <button type="button" className="btn-ghost h-7 px-2 text-xs" disabled={actions.pending} onClick={() => run(actions.update(ep.id, { enabled: false }))}>
                        Выключить
                      </button>
                    ) : (
                      !needsPing && (
                        <button type="button" className="btn-ghost h-7 px-2 text-xs" disabled={actions.pending} onClick={() => run(actions.update(ep.id, { enabled: true }))}>
                          Включить
                        </button>
                      )
                    )}
                    <button type="button" className="btn-ghost h-7 px-2 text-xs" disabled={actions.pending || !ep.enabled} title={ep.enabled ? undefined : 'Сначала включите вебхук'} onClick={() => setPeriod(period === ep.id ? null : ep.id)}>
                      Повторить неудачные за период
                    </button>
                    <ConfirmButton label="Новый секрет" className="btn-ghost h-7 px-2 text-xs" title="Выпустить новый секрет?" confirmLabel="Выпустить" disabled={actions.pending} onConfirm={() => run(actions.rotate(ep.id).then((x) => setIssued({ ep: x, rotated: true })))}>
                      Новый секрет покажем один раз. Старый ещё 24 часа подписывает события рядом с новым, чтобы вы успели заменить его на сервере.
                    </ConfirmButton>
                    <ConfirmButton label="Удалить" className="btn-ghost h-7 px-2 text-xs" title="Удалить вебхук?" confirmLabel="Удалить" quiet disabled={actions.pending} onConfirm={() => run(actions.remove(ep.id))}>
                      События перестанут уходить на этот адрес, журнал доставок удалится. Вернуть нельзя, только добавить заново.
                    </ConfirmButton>
                  </div>
                )}
                {actions && period === ep.id && (
                  <ReplayPeriod
                    pending={actions.pending}
                    onCancel={() => setPeriod(null)}
                    onReplay={(since, until) =>
                      run(
                        actions.replayFailed(ep.id, since, until).then((r) => {
                          setPeriod(null);
                          note(ep.id, r.queued ? `В очереди на повтор: ${r.queued}.` : 'Неудачных доставок за период нет.');
                        }),
                      )
                    }
                  />
                )}
                {onDisable && ep.enabled && (
                  <div className="mt-2">
                    <ConfirmButton label="Выключить точку" className="btn-ghost h-7 px-2 text-xs" title="Выключить вебхук клиента?" confirmLabel="Выключить" quiet disabled={disabling} onConfirm={() => run(onDisable(ep.id))}>
                      События перестанут уходить на этот адрес, клиенту придёт уведомление. Включится снова, когда клиент нажмёт «Проверить» и сервер ответит 2xx.
                    </ConfirmButton>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {problem && (
        <p role="alert" className="mt-2 text-sm text-overdue">
          {problem}
        </p>
      )}
      {actions &&
        (adding ? (
          <div className="mt-4">
            <EndpointForm events={events} pending={actions.pending} onCancel={() => setAdding(false)} onSave={(v) => run(actions.create(v).then((x) => (setAdding(false), setIssued({ ep: x }))))} />
          </div>
        ) : (
          <button className="btn-secondary mt-3" onClick={() => setAdding(true)}>
            Добавить вебхук
          </button>
        ))}
      <WebhookSecretDialog issued={issued?.ep ?? null} rotated={issued?.rotated} onClose={() => setIssued(null)} />
    </>
  );
}

const STATUSES: WebhookDeliveryStatus[] = ['delivered', 'failed', 'dead', 'pending'];

/** The deliveries log, newest first; `onReplay` adds «Повторить». The owner of the data (page, filter) is the caller. */
export function WebhookLog({
  page,
  filter,
  onFilter,
  onOlder,
  onNewer,
  onReplay,
  replaying,
  endpoints,
  events = OFFERED_EVENTS,
}: {
  page: WebhookDeliveriesPage | undefined;
  filter: WebhookDeliveryFilter;
  onFilter: (f: WebhookDeliveryFilter) => void;
  onOlder: () => void;
  onNewer?: () => void;
  onReplay?: (id: string) => Promise<unknown>;
  replaying?: boolean;
  endpoints: WebhookEndpoint[];
  events?: string[];
}) {
  const [problem, setProblem] = useState('');
  const [queued, setQueued] = useState<Set<string>>(new Set());
  const enabled = new Set(endpoints.filter((e) => e.enabled).map((e) => e.id));
  const replay = (d: WebhookDelivery) =>
    onReplay &&
    (setProblem(''),
    onReplay(d.id)
      .then(() => setQueued((q) => new Set(q).add(d.id)))
      .catch((e: Error) => setProblem(e.message)));
  return (
    <div data-testid="webhook-log">
      <div className="mb-2 flex flex-wrap gap-2 text-sm">
        <label className="flex items-center gap-1.5">
          <span className="text-muted">Статус</span>
          <select className="field h-8 w-auto" value={filter.status ?? ''} onChange={(e) => onFilter({ ...filter, status: (e.target.value || undefined) as WebhookDeliveryStatus | undefined, before: undefined })}>
            <option value="">все</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          <span className="text-muted">Событие</span>
          <select className="field h-8 w-auto" value={filter.event ?? ''} onChange={(e) => onFilter({ ...filter, event: e.target.value || undefined, before: undefined })}>
            <option value="">все</option>
            {events.map((t) => (
              <option key={t} value={t}>
                {eventLabel(t)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!page ? null : page.items.length === 0 ? (
        <p className="text-sm text-muted">Доставок нет.</p>
      ) : (
        <>
          <table className="w-full text-left text-xs">
            <thead className="text-muted">
              <tr>
                <th className="py-1 font-normal">Когда</th>
                <th className="py-1 font-normal">Событие</th>
                <th className="py-1 text-right font-normal">Попытка</th>
                <th className="py-1 text-right font-normal">Код</th>
                <th className="py-1 text-right font-normal">Время</th>
                <th className="py-1 pl-3 font-normal">Статус</th>
                {onReplay && <th className="py-1" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-line border-y border-line">
              {page.items.map((d) => {
                const st = deliveryState(d);
                return (
                  <tr key={d.id} data-testid={`delivery-${d.id}`}>
                    <td className="py-1.5 pr-2 whitespace-nowrap">{whenLabel(d.createdAt)}</td>
                    <td className="py-1.5 pr-2">
                      {eventLabel(d.type)}
                      {d.replayOf && <span className="text-faint"> · повтор</span>}
                    </td>
                    <td className="num py-1.5 pr-2 text-right">{d.attempt}</td>
                    <td className={d.status === 'delivered' ? 'num py-1.5 pr-2 text-right' : 'num py-1.5 pr-2 text-right text-overdue'}>{answerLabel(d)}</td>
                    <td className="num py-1.5 text-right">{d.durationMs === null ? '—' : `${d.durationMs} мс`}</td>
                    <td className="py-1.5 pl-3">
                      <Status tone={st.tone} className="text-xs">
                        {st.label}
                      </Status>
                    </td>
                    {onReplay && (
                      <td className="py-1.5 text-right">
                        {replayable(d) &&
                          (queued.has(d.id) ? (
                            <span className="text-muted">в очереди</span>
                          ) : (
                            <button type="button" className="btn-ghost h-6 px-2 text-xs" disabled={replaying || !enabled.has(d.endpointId)} title={enabled.has(d.endpointId) ? undefined : 'Вебхук выключен'} onClick={() => replay(d)}>
                              Повторить
                            </button>
                          ))}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-2 flex items-center gap-2 text-xs">
            {onNewer && (
              <button className="btn-ghost h-7 px-2 text-xs" onClick={onNewer}>
                Новее
              </button>
            )}
            <button className="btn-ghost h-7 px-2 text-xs" disabled={!page.nextBefore} onClick={onOlder}>
              Раньше
            </button>
            <span className="text-muted">Журнал хранится 30 дней.</span>
          </div>
        </>
      )}
      {problem && (
        <p role="alert" className="mt-2 text-sm text-overdue">
          {problem}
        </p>
      )}
    </div>
  );
}

